import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc.js";
import { Artefato } from "../../components/Artefato.js";
import { Botao, Campo, Entrada, Falha } from "../../components/ui.js";
import { useAuth } from "./useAuth.js";
import { BotaoGoogle } from "./BotaoGoogle.js";

/**
 * Cadastro que a própria pessoa faz.
 *
 * Dois passos numa tela: os dados, depois o código. A conta existe já no
 * primeiro passo, mas sem sessão — quem não confirmar o contato não entra.
 */
export function CadastroPage() {
  const { autenticado, carregando } = useAuth();
  const queryClient = useQueryClient();

  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [codigo, setCodigo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const [canal, setCanal] = useState<"whatsapp" | "email" | null>(null);
  const [reenviado, setReenviado] = useState(false);
  const [trocandoNumero, setTrocandoNumero] = useState(false);

  const cadastrar = trpc.auth.cadastrar.useMutation({
    onSuccess: (r) => {
      setErro(null);
      setCanal(r.canal);
    },
    onError: (e) => setErro(e.message),
  });

  const confirmar = trpc.auth.confirmarCadastro.useMutation({
    onSuccess: async () => {
      setErro(null);
      await queryClient.invalidateQueries();
    },
    onError: (e) => setErro(e.message),
  });

  const corrigir = trpc.auth.corrigirWhatsapp.useMutation({
    onSuccess: (r) => {
      setErro(null);
      setCanal(r.canal);
      setTrocandoNumero(false);
      setReenviado(true);
      // O código anterior foi invalidado ao emitir o novo: deixar o campo
      // preenchido convidaria a tentar o velho e ouvir que está errado.
      setCodigo("");
    },
    onError: (e) => setErro(e.message),
  });

  const reenviar = trpc.auth.reenviarCodigo.useMutation({
    onSuccess: (r) => {
      setErro(null);
      setCanal(r.canal);
      setReenviado(true);
    },
    onError: (e) => setErro(e.message),
  });

  // Quem já entrou não tem o que fazer aqui.
  if (!carregando && autenticado) return <Navigate to="/nova" replace />;

  const noPassoDoCodigo = canal !== null;

  function enviarDados(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    cadastrar.mutate({
      name: nome.trim(),
      email: email.trim().toLowerCase(),
      password: senha,
      whatsapp,
    });
  }

  function enviarCodigo(e: FormEvent) {
    e.preventDefault();
    setErro(null);
    confirmar.mutate({ codigo: codigo.trim() });
  }

  return (
    <main className="flex min-h-full flex-col justify-center px-6 py-12">
      <div className="mx-auto w-full max-w-sm">
        <div className="flex items-center gap-3.5">
          <Artefato tamanho="tira" estado="vazio" rotulos={false} className="h-14 [&>div]:h-14" />
          <div>
            <h1 className="font-display text-[1.75rem] leading-none tracking-[-0.02em]">
              Criar conta
            </h1>
            <p className="mt-1.5 text-[0.9375rem] text-texto-suave">
              {noPassoDoCodigo
                ? "Confirme o código que enviamos."
                : "Comece a fazer os seus eventos."}
            </p>
          </div>
        </div>

        {!noPassoDoCodigo ? (
          <>
            {/* Primeiro o Google: um clique, sem senha para inventar e sem
                código para esperar — o Google já confirmou o e-mail. */}
            <div className="mt-8">
              <BotaoGoogle texto="continue_with" divisor="depois" />
            </div>

            <form onSubmit={enviarDados} className="mt-4 flex flex-col gap-4">
              <Campo rotulo="Seu nome">
                <Entrada
                  value={nome}
                  onChange={(e) => setNome(e.target.value)}
                  autoComplete="name"
                  required
                  disabled={cadastrar.isPending}
                />
              </Campo>

              <Campo rotulo="E-mail">
                <Entrada
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  required
                  disabled={cadastrar.isPending}
                />
              </Campo>

              <Campo rotulo="WhatsApp" dica="É por aqui que enviamos o código de confirmação.">
                <Entrada
                  type="tel"
                  inputMode="tel"
                  value={whatsapp}
                  onChange={(e) => setWhatsapp(e.target.value)}
                  autoComplete="tel"
                  placeholder="(31) 99999-9999"
                  required
                  disabled={cadastrar.isPending}
                />
              </Campo>

              <Campo rotulo="Senha" dica="Ao menos 8 caracteres.">
                <Entrada
                  type="password"
                  value={senha}
                  onChange={(e) => setSenha(e.target.value)}
                  autoComplete="new-password"
                  required
                  disabled={cadastrar.isPending}
                />
              </Campo>

              {erro && <Falha mensagem={erro} />}

              <Botao
                type="submit"
                tom="brasa"
                largo
                carregando={cadastrar.isPending}
                className="mt-2"
              >
                Criar conta
              </Botao>
            </form>
          </>
        ) : trocandoNumero ? (
          <div className="mt-8 flex flex-col gap-4">
            <p className="text-[0.9375rem] leading-relaxed text-texto-suave">
              Confira o número e mande o código de novo.
            </p>

            <Campo rotulo="Número de WhatsApp">
              <Entrada
                type="tel"
                inputMode="tel"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                autoComplete="tel"
                placeholder="(31) 99999-9999"
                disabled={corrigir.isPending}
              />
            </Campo>

            {erro && <Falha mensagem={erro} />}

            <div className="flex gap-2">
              <Botao
                tom="quieto"
                className="flex-1"
                onClick={() => {
                  setTrocandoNumero(false);
                  setErro(null);
                }}
              >
                Voltar
              </Botao>
              <Botao
                tom="brasa"
                className="flex-1"
                carregando={corrigir.isPending}
                onClick={() => corrigir.mutate({ whatsapp })}
              >
                Enviar de novo
              </Botao>
            </div>
          </div>
        ) : (
          <form onSubmit={enviarCodigo} className="mt-8 flex flex-col gap-4">
            <p className="text-[0.9375rem] leading-relaxed text-texto-suave">
              {canal === "whatsapp"
                ? "Enviamos um código de 6 dígitos para o seu WhatsApp."
                : "Enviamos um código de 6 dígitos para o seu e-mail."}
            </p>

            <Campo rotulo="Código">
              <Entrada
                inputMode="numeric"
                autoComplete="one-time-code"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                required
                disabled={confirmar.isPending}
                className="text-center text-[1.5rem] tracking-[0.3em]"
              />
            </Campo>

            {erro && <Falha mensagem={erro} />}

            <Botao
              type="submit"
              tom="brasa"
              largo
              carregando={confirmar.isPending}
              disabled={codigo.length !== 6}
              className="mt-2"
            >
              Confirmar
            </Botao>

            {/* O WhatsApp aceita a mensagem sem garantir a entrega: um número
                digitado errado não devolve erro nenhum. Sem esta saída, a
                pessoa ficaria presa esperando um código que nunca vem. */}
            <div className="flex flex-col gap-2 pt-1 text-center">
              <button
                type="button"
                onClick={() => reenviar.mutate({})}
                disabled={reenviar.isPending}
                className="text-[0.875rem] text-texto-suave underline underline-offset-4 transition-colors hover:text-texto disabled:opacity-50"
              >
                Reenviar o código
              </button>

              {canal === "whatsapp" && (
                <button
                  type="button"
                  onClick={() => reenviar.mutate({ porEmail: true })}
                  disabled={reenviar.isPending}
                  className="text-[0.875rem] text-texto-suave underline underline-offset-4 transition-colors hover:text-texto disabled:opacity-50"
                >
                  Não recebi — mandar por e-mail
                </button>
              )}

              {/* Errar um dígito do telefone é o jeito mais comum de travar um
                  cadastro, e as outras duas saídas não resolvem: reenviar
                  repete o erro, e o e-mail confirma a conta com o número
                  errado gravado — no canal pelo qual a pessoa será atendida. */}
              <button
                type="button"
                onClick={() => {
                  setTrocandoNumero(true);
                  setReenviado(false);
                }}
                className="text-[0.875rem] text-texto-suave underline underline-offset-4 transition-colors hover:text-texto"
              >
                Errei o número
              </button>

              {reenviado && !erro && (
                <p className="text-[0.8125rem] text-fixado">
                  {canal === "whatsapp" ? "Enviado pelo WhatsApp." : "Enviado por e-mail."}
                </p>
              )}
            </div>
          </form>
        )}

        <p className="mt-8 text-center text-[0.875rem] text-texto-suave">
          Já tem conta?{" "}
          <Link to="/entrar" className="text-brasa underline underline-offset-4">
            Entrar
          </Link>
        </p>
      </div>
    </main>
  );
}
